/**
 * FunnelBoard — Email Funnels Kanban with native HTML5 drag-and-drop.
 *
 * Cards are draggable between stage columns. On drop the board updates
 * optimistically and calls POST /interaction/:id/funnel to persist.
 * Click a card to jump to that email in the Emails tab.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  AccountTree as FunnelIcon,
  DragIndicator as DragIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { SupportFunnel, FunnelBoardColumn, FunnelBoardCard } from './types';
import { apiService } from '../../services/api';
import FunnelEditorDialog from './FunnelEditorDialog';

const DEEP_TEAL = '#1A5F7A';

interface DragState {
  card: FunnelBoardCard;
  fromStageId: string;
}

interface FunnelBoardProps {
  onOpenEmail: (externalId: string) => void;
}

const FunnelBoard: React.FC<FunnelBoardProps> = ({ onOpenEmail }) => {
  const [funnels, setFunnels] = useState<SupportFunnel[]>([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(null);
  const [board, setBoard] = useState<FunnelBoardColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const [boardLoading, setBoardLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState<SupportFunnel | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // DnD state
  const dragging = useRef<DragState | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);

  const loadFunnels = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getSupportFunnels();
      setFunnels(data as SupportFunnel[]);
      if (data.length > 0 && !selectedFunnelId) {
        setSelectedFunnelId((data[0] as SupportFunnel).id);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [selectedFunnelId]);

  const loadBoard = useCallback(async (funnelId: string) => {
    setBoardLoading(true);
    try {
      const data = await apiService.getSupportFunnelBoard(funnelId);
      setBoard((data?.board ?? []) as FunnelBoardColumn[]);
    } catch {
      setBoard([]);
    } finally {
      setBoardLoading(false);
    }
  }, []);

  useEffect(() => { loadFunnels(); }, []);
  useEffect(() => {
    if (selectedFunnelId) loadBoard(selectedFunnelId);
  }, [selectedFunnelId, loadBoard]);

  const handleEditorSaved = () => {
    setEditorOpen(false);
    setEditingFunnel(null);
    loadFunnels();
    if (selectedFunnelId) loadBoard(selectedFunnelId);
  };

  // Called after FunnelEditorDialog already made the API delete call — only update local state.
  const handleFunnelDeleted = (id: string) => {
    const remaining = funnels.filter(f => f.id !== id);
    setFunnels(remaining);
    if (selectedFunnelId === id) {
      setSelectedFunnelId(remaining.length > 0 ? remaining[0].id : null);
      setBoard([]);
    }
  };

  // ── Drag handlers ────────────────────────────────────────────────────────────

  const handleDragStart = (card: FunnelBoardCard, fromStageId: string) =>
    (e: React.DragEvent) => {
      dragging.current = { card, fromStageId };
      // Ghost image: the card element itself (browser default) — no extra setup
      e.dataTransfer.effectAllowed = 'move';
    };

  const handleDragOver = (stageId: string) =>
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverStageId(stageId);
    };

  const handleDragLeave = () => setDragOverStageId(null);

  const handleDragEnd = () => {
    dragging.current = null;
    setDragOverStageId(null);
  };

  const handleDrop = (targetStageId: string, targetFunnelId: string) =>
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragOverStageId(null);

      const state = dragging.current;
      dragging.current = null;

      if (!state || state.fromStageId === targetStageId) return;

      const { card, fromStageId } = state;

      // Optimistic update: move card from source column to target column
      setBoard(prev => {
        const next = prev.map(col => {
          if (col.id === fromStageId) {
            return { ...col, emails: col.emails.filter(c => c.assignment_id !== card.assignment_id) };
          }
          if (col.id === targetStageId) {
            const movedCard: FunnelBoardCard = { ...card, stage_id: targetStageId };
            return { ...col, emails: [movedCard, ...col.emails] };
          }
          return col;
        });
        return next;
      });

      // Persist
      try {
        await apiService.assignInteractionToFunnel(card.external_id, {
          funnelId: targetFunnelId,
          stageId: targetStageId,
        });
      } catch {
        // Revert on failure
        setErrorMsg('Move failed — please try again');
        if (selectedFunnelId) loadBoard(selectedFunnelId);
      }
    };

  const selectedFunnel = funnels.find(f => f.id === selectedFunnelId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Funnel selector bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          funnels.map(f => (
            <Chip
              key={f.id}
              label={f.name}
              onClick={() => setSelectedFunnelId(f.id)}
              icon={<FunnelIcon sx={{ fontSize: '14px !important', color: `${f.color} !important` }} />}
              variant={selectedFunnelId === f.id ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedFunnelId === f.id ? `${f.color}22` : undefined,
                borderColor: f.color,
                color: selectedFunnelId === f.id ? f.color : 'text.primary',
                fontWeight: selectedFunnelId === f.id ? 700 : 400,
                cursor: 'pointer',
              }}
            />
          ))
        )}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => { setEditingFunnel(null); setEditorOpen(true); }}
          sx={{ textTransform: 'none', color: DEEP_TEAL, borderColor: DEEP_TEAL }}
          variant="outlined"
        >
          New Funnel
        </Button>
        {selectedFunnel && (
          <Tooltip title="Edit funnel / stages">
            <IconButton size="small" onClick={() => { setEditingFunnel(selectedFunnel); setEditorOpen(true); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {selectedFunnelId && (
          <Tooltip title="Refresh board">
            <Button
              size="small"
              onClick={() => loadBoard(selectedFunnelId)}
              sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary', ml: 'auto' }}
            >
              Refresh
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Board */}
      {!selectedFunnelId ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <FunnelIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">Create a funnel to get started</Typography>
          </Box>
        </Box>
      ) : boardLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flex: 1, overflowX: 'auto', overflowY: 'hidden', pb: 1, userSelect: 'none' }}>
          {board.map(col => {
            const isOver = dragOverStageId === col.id;
            const colColor = col.color || DEEP_TEAL;
            return (
              <Paper
                key={col.id}
                elevation={0}
                onDragOver={handleDragOver(col.id)}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop(col.id, selectedFunnelId!)}
                sx={{
                  width: 280,
                  minWidth: 280,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '2px solid',
                  borderColor: isOver ? colColor : 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'border-color 0.15s',
                  bgcolor: isOver ? `${colColor}06` : 'background.paper',
                }}
              >
                {/* Column header */}
                <Box sx={{ px: 2, py: 1.5, bgcolor: `${colColor}18`, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: colColor }}>
                      {col.name}
                    </Typography>
                    <Chip
                      label={col.emails.length}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${colColor}30`, color: colColor, fontWeight: 700 }}
                    />
                  </Box>
                </Box>

                {/* Cards */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    minHeight: 80,
                    // Drop zone hint when empty and hovered
                    ...(isOver && col.emails.length === 0 && {
                      outline: `2px dashed ${colColor}`,
                      outlineOffset: -4,
                      borderRadius: 1,
                    }),
                  }}
                >
                  {col.emails.length === 0 && !isOver ? (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ textAlign: 'center', pt: 2, fontStyle: 'italic', userSelect: 'none' }}
                    >
                      Drop emails here
                    </Typography>
                  ) : (
                    col.emails.map(card => (
                      <KanbanCard
                        key={card.assignment_id}
                        card={card}
                        colColor={colColor}
                        onDragStart={handleDragStart(card, col.id)}
                        onDragEnd={handleDragEnd}
                        onOpen={() => onOpenEmail(card.external_id)}
                      />
                    ))
                  )}
                </Box>
              </Paper>
            );
          })}

          {board.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Typography color="text.secondary">
                No stages yet — edit the funnel to add stages
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <FunnelEditorDialog
        open={editorOpen}
        funnel={editingFunnel}
        onClose={() => { setEditorOpen(false); setEditingFunnel(null); }}
        onSaved={handleEditorSaved}
        onDeleted={(id) => { handleFunnelDeleted(id); setEditorOpen(false); setEditingFunnel(null); }}
      />

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ── KanbanCard sub-component ─────────────────────────────────────────────────

interface KanbanCardProps {
  card: FunnelBoardCard;
  colColor: string;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onOpen: () => void;
}

const KanbanCard: React.FC<KanbanCardProps> = ({ card, colColor, onDragStart, onDragEnd, onOpen }) => {
  const [isDragging, setIsDragging] = useState(false);

  const ageLabel = (() => {
    const ms = Date.now() - new Date(card.received_at).getTime();
    const h = Math.floor(ms / 3600000);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  })();

  return (
    <Paper
      draggable
      onDragStart={(e) => { setIsDragging(true); onDragStart(e); }}
      onDragEnd={() => { setIsDragging(false); onDragEnd(); }}
      variant="outlined"
      sx={{
        p: 1.5,
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity 0.15s, box-shadow 0.15s',
        borderRadius: 1,
        borderColor: isDragging ? colColor : 'divider',
        '&:hover': { boxShadow: 2, borderColor: colColor },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {/* Drag handle row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <DragIcon sx={{ fontSize: 14, color: 'text.disabled', mt: 0.25, flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: '0.8rem', flex: 1, lineHeight: 1.3 }}
          noWrap
          title={card.subject || undefined}
        >
          {card.subject || '(No subject)'}
        </Typography>
        <Tooltip title="Open email">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: colColor } }}
          >
            <OpenIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Sender */}
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ display: 'block', pl: 2.5, fontSize: '0.72rem' }}
      >
        {card.member_name || card.from_address}
      </Typography>

      {/* Footer: tags + age */}
      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, pl: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {card.tags?.slice(0, 2).map(tag => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{ height: 15, fontSize: '0.6rem', bgcolor: `${colColor}15`, color: colColor, '.MuiChip-label': { px: 0.5 } }}
          />
        ))}
        {card.is_resolved && (
          <Chip label="Resolved" size="small" color="success" sx={{ height: 15, fontSize: '0.6rem' }} />
        )}
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.65rem', flexShrink: 0 }}>
          {ageLabel}
        </Typography>
      </Box>
    </Paper>
  );
};

export default FunnelBoard;
